// Weekly opening hours, shared by the API, the admin panel and the public site.
// All "now" calculations happen in Europe/Bucharest, independent of the server's
// or the visitor's time zone.

export interface DaySchedule {
  closed: boolean;
  open: string;  // "HH:MM"
  close: string; // "HH:MM"
}

// 7 entries, index 0 = Monday ... 6 = Sunday
export type WeeklySchedule = DaySchedule[];

export type ScheduleLanguage = 'ro' | 'en';

export const STORE_TIME_ZONE = 'Europe/Bucharest';

export const DAY_NAMES: Record<ScheduleLanguage, string[]> = {
  ro: ['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};

export const DEFAULT_SCHEDULE: WeeklySchedule = [
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '10:00', close: '18:00' },
];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const WEEKDAY_INDEX: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/** Day index (0 = Monday) and minutes since midnight in the store's time zone. */
export function getBucharestNow(date: Date = new Date()): { dayIndex: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: STORE_TIME_ZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    dayIndex: WEEKDAY_INDEX[get('weekday')] ?? 0,
    minutes: Number(get('hour')) * 60 + Number(get('minute')),
  };
}

/** Open from `open` (inclusive) until `close` (exclusive). */
export function isOpenAt(schedule: WeeklySchedule, date: Date = new Date()): boolean {
  const { dayIndex, minutes } = getBucharestNow(date);
  const day = schedule[dayIndex];
  if (!day || day.closed) return false;
  return minutes >= toMinutes(day.open) && minutes < toMinutes(day.close);
}

export interface NextOpening {
  dayIndex: number;
  time: string;
  relative: 'today' | 'tomorrow' | 'later';
}

/** The next moment the store opens, or null if every day is closed. */
export function getNextOpening(schedule: WeeklySchedule, date: Date = new Date()): NextOpening | null {
  const { dayIndex, minutes } = getBucharestNow(date);
  for (let offset = 0; offset < 8; offset++) {
    const idx = (dayIndex + offset) % 7;
    const day = schedule[idx];
    if (!day || day.closed) continue;
    if (offset === 0 && minutes >= toMinutes(day.open)) continue;
    return {
      dayIndex: idx,
      time: day.open,
      relative: offset === 0 ? 'today' : offset === 1 ? 'tomorrow' : 'later',
    };
  }
  return null;
}

/** Groups consecutive days with identical hours, e.g. "Luni – Sâmbătă: 09:00 – 20:00". */
export function formatScheduleLines(schedule: WeeklySchedule, lang: ScheduleLanguage): string[] {
  const names = DAY_NAMES[lang];
  const closedLabel = lang === 'ro' ? 'Închis' : 'Closed';
  const hoursOf = (d: DaySchedule) => (d.closed ? closedLabel : `${d.open} – ${d.close}`);

  const lines: string[] = [];
  let start = 0;
  for (let i = 1; i <= schedule.length; i++) {
    if (i < schedule.length && hoursOf(schedule[i]) === hoursOf(schedule[start])) continue;
    const label = start === i - 1 ? names[start] : `${names[start]} – ${names[i - 1]}`;
    lines.push(`${label}: ${hoursOf(schedule[start])}`);
    start = i;
  }
  return lines;
}

export type ScheduleValidation =
  | { ok: true; schedule: WeeklySchedule }
  | { ok: false; error: string; dayIndex?: number };

export function validateSchedule(input: unknown): ScheduleValidation {
  if (!Array.isArray(input) || input.length !== 7) {
    return { ok: false, error: 'Programul trebuie să conțină exact 7 zile' };
  }

  const schedule: WeeklySchedule = [];
  for (let i = 0; i < 7; i++) {
    const day = input[i] as Partial<DaySchedule> | null;
    const dayName = DAY_NAMES.ro[i];
    if (!day || typeof day !== 'object' || typeof day.closed !== 'boolean') {
      return { ok: false, error: `${dayName}: date invalide`, dayIndex: i };
    }
    if (typeof day.open !== 'string' || !TIME_RE.test(day.open) ||
        typeof day.close !== 'string' || !TIME_RE.test(day.close)) {
      return { ok: false, error: `${dayName}: ora trebuie să fie în formatul HH:MM`, dayIndex: i };
    }
    if (!day.closed && toMinutes(day.close) <= toMinutes(day.open)) {
      return { ok: false, error: `${dayName}: ora de închidere trebuie să fie după ora de deschidere`, dayIndex: i };
    }
    schedule.push({ closed: day.closed, open: day.open, close: day.close });
  }
  return { ok: true, schedule };
}
